<script>
import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import request from '@/services/request';
import { currencies } from '@/services/currencies';
import { widgetViews } from '@/utils';
import { getUpsellDecision } from '@/services/upsell';
import Iframe from '@/widgets/form/Iframe.vue';
import CheckoutLayout from '@/layouts/CheckoutLayout.vue';
import AppButton from '@/common/AppButton.vue';
import FormLayout from '@/layouts/FormLayout.vue';
import LocalizationButton from '@/components/LocalizationButton.vue';
import FormOften from '@/components/FormOften.vue';
import FormMainDetails from '@/components/FormMainDetails.vue';
import FormDonationItems from '@/components/FormDonationItems.vue';
import FormEndDate from '@/components/FormEndDate.vue';

import { setCampaignInUrl, sleep, checkShouldSkipAdditionalDetails } from '@/utils';

export default {
  name: 'FormApp',

  components: {
    CheckoutLayout,
    Iframe,
    AppButton,
    FormLayout,
    LocalizationButton,
    FormOften,
    FormMainDetails,
    FormDonationItems,
    FormEndDate
  },

  props: {
    campaignSlug: {
      type: String,
      require: false
    }
  },

  data: () => ({
    campaignSettings: null, // Important to set to null, we use this to check if campaign settings are loaded or found at all
    campaignId: null,
    localAmount: null,
    localFrequency: null,
    localStartDate: null,
    localNumCharges: null,
    localDonationItems: [],
    localDonationItemsOtherAmount: null
  }),

  computed: {
    ...mapState(['orgId']),
    ...mapState('publicOrgSettings', ['settings']),
    ...mapState('donation', {
      donation: (state) => state
    }),
    ...mapGetters('validation', ['isFirstStepValid']),
    settingsComputed() {
      if (this.campaignSettings) {
        return this.campaignSettings;
      }

      return this.settings;
    },
    showForm() {
      if (!this.campaignSlug) {
        return true;
      }

      return !!this.campaignSettings?.id;
    },
    hideFrequencyToggle() {
      if (this.isItemsCheckout) {
        return true;
      }

      return this.settingsComputed?.frequenciesEnabled?.length < 2;
    },
    checkoutMode() {
      return this.settingsComputed?.checkoutMode || 'amounts';
    },
    hasDonationItems() {
      return Boolean(this.settingsComputed?.donationItems?.length);
    },
    isItemsCheckout() {
      return this.checkoutMode === 'items' && this.hasDonationItems;
    },
    hideDurationSettings() {
      return this.isItemsCheckout || this.settingsComputed?.hideDurationSettings;
    },
    donateButtonText() {
      const customCtaText = this.settingsComputed?.customCtaText?.trim();
      if (customCtaText) {
        return customCtaText;
      }

      const ctaTerm = this.settingsComputed?.ctaTerm?.toLowerCase();
      if (!ctaTerm) {
        return this.$t('donorWall.cta.donate');
      }
      return this.$t(`donorWall.cta.${ctaTerm}`);
    },
    shouldSkipAdditionalDetails() {
      return checkShouldSkipAdditionalDetails(this.settingsComputed || {}, this.donation);
    },
    upsellDecision() {
      return getUpsellDecision({
        settings: this.settingsComputed,
        orgUpsellSettings: this.settings?.upsell,
        donation: this.donation,
        campaignId: this.campaignId || this.donation.campaignId,
        campaignCurrency: this.campaignSettings?.currency || this.settings?.campaignCurrency
      });
    },
    hasHandledUpsell() {
      return this.donation.upsell?.originalAmount === this.donation.amount;
    }
  },

  watch: {
    isItemsCheckout: {
      immediate: true,
      handler(isItemsCheckout) {
        if (isItemsCheckout && this.localFrequency !== 'single') {
          this.localFrequency = 'single';
        }
      }
    },
    localFrequency(newValue, oldValue) {
      if (oldValue && oldValue !== newValue) {
        this.localAmount = null;
      }
    }
  },

  methods: {
    ...mapActions(['setWidgetOpen']),
    ...mapMutations(['SET_WIDGET_VIEW', 'SET_SHOW_PAYMENT_BUTTONS']),
    ...mapMutations('donation', [
      'SET_AMOUNT',
      'RESET_AMOUNT',
      'SET_AMOUNT_DESCRIPTION',
      'SET_START_DATE',
      'SET_NUM_CHARGES',
      'SET_DONATION_ITEM_QUANTITY',
      'RESET_DONATION_ITEMS',
      'SET_DONATION_ITEMS_OTHER_AMOUNT'
    ]),
    ...mapActions('donation', ['SET_CURRENCY_ACTIONS', 'SET_FREQUENCY_ACTIONS']),
    setCampaignInUrl,
    async donateButtonClicked() {
      // Sync local state to Vuex before opening checkout.
      if (this.isItemsCheckout) {
        this.RESET_DONATION_ITEMS();
        this.localDonationItems.forEach((item) => {
          this.SET_DONATION_ITEM_QUANTITY({ item, quantity: item.quantity });
        });
        this.SET_DONATION_ITEMS_OTHER_AMOUNT(this.localDonationItemsOtherAmount);
        if (this.localAmount) {
          this.SET_AMOUNT(this.localAmount);
        } else {
          this.RESET_AMOUNT();
        }
        this.SET_AMOUNT_DESCRIPTION(null);
        this.SET_FREQUENCY_ACTIONS('single');
      } else {
        this.RESET_DONATION_ITEMS();
        if (this.localAmount) {
          this.SET_AMOUNT(this.localAmount);
        } else {
          this.RESET_AMOUNT();
        }
        if (this.localFrequency) this.SET_FREQUENCY_ACTIONS(this.localFrequency);
      }

      if (!this.isItemsCheckout && this.localFrequency !== 'single') {
        if (this.localStartDate) {
          this.SET_START_DATE(this.localStartDate);
        }
        if (Number.isFinite(this.localNumCharges)) {
          this.SET_NUM_CHARGES(this.localNumCharges);
        }
      }

      this.setCampaignInUrl(this.campaignSlug || this.settings.defaultSlug);
      await this.setWidgetOpen(true);
      // check if amount is valid using the validation module
      if (this.isFirstStepValid) {
        if (this.upsellDecision && !this.hasHandledUpsell) {
          this.SET_WIDGET_VIEW(widgetViews.UPSELL);
          return;
        }

        // Skip additional details if all options are hidden
        if (this.shouldSkipAdditionalDetails) {
          this.SET_SHOW_PAYMENT_BUTTONS(true);
          return;
        }
        this.SET_WIDGET_VIEW(widgetViews.ADDITIONAL_DETAILS);
      }
      if (this.settingsComputed?.formWidgetOpenSummary && this.donation.amount) {
        await sleep(500);
        this.SET_SHOW_PAYMENT_BUTTONS(true);
      }
    }
  },

  async mounted() {
    // If no campaign slug is passed in form will use default campaign settings
    if (!this.campaignSlug) {
      this.localFrequency = this.settings?.frequencyDefaultComputed || 'single';
      return;
    }
    const { data: campaign, error } = await request.get(`/public/campaign/${this.orgId}/${this.campaignSlug}`);
    if (error) {
      console.error('Error fetching campaign', error);
      return;
    }

    this.campaignSettings = { ...campaign?.checkoutSettings, currency: campaign?.currency };
    this.campaignId = campaign?.id;
    this.localFrequency = this.campaignSettings?.frequencyDefaultComputed || 'single';

    if (campaign.checkoutSettings?.disabledCurrencies?.includes(this.donation.currency)) {
      // TODO: We should have a checkout_settings for defaultCurrency
      const defaultEnabledCurrency = currencies.value.find((currency) => !campaign.checkoutSettings?.disabledCurrencies?.includes(currency.value)).value;
      this.SET_CURRENCY_ACTIONS(defaultEnabledCurrency || 'usd'); // todo should be based on default currency that is set in settings
    }
  }
};
</script>

<template>
  <div class="double--form-widget-frame">
    <Iframe v-if="showForm">
      <checkout-layout hide-header static-form :form-campaign-settings="campaignSettings">
        <div class="static-form form-container">
          <form-layout static-form>
            <localization-button
              @click.native="
                setCampaignInUrl(campaignSlug || settings.defaultSlug);
                setWidgetOpen(true);
              "
              delay-modal
              static-form
              class="flex justify-start"
              theme="light"
            />
            <div class="app-form">
              <div class="form-container">
                <div class="form-content">
                  <form-often
                    v-if="!hideFrequencyToggle"
                    static-form
                    :form-campaign-settings="campaignSettings"
                    :local-frequency="localFrequency"
                    @update:local-frequency="localFrequency = $event"
                  />
                  <transition name="expandx2" v-if="!hideDurationSettings">
                    <form-end-date
                      static-form
                      :form-campaign-settings="campaignSettings"
                      :local-frequency="localFrequency"
                      :local-start-date="localStartDate"
                      :local-num-charges="localNumCharges"
                      @update:local-start-date="localStartDate = $event"
                      @update:local-num-charges="localNumCharges = $event"
                    />
                  </transition>
                  <form-main-details
                    v-if="!isItemsCheckout"
                    :active="true"
                    static-form
                    :form-campaign-settings="campaignSettings"
                    :local-amount="localAmount"
                    :local-frequency="localFrequency"
                    @update:local-amount="localAmount = $event"
                  />
                  <form-donation-items
                    v-else
                    :active="true"
                    static-form
                    :form-campaign-settings="campaignSettings"
                    :local-donation-items="localDonationItems"
                    :local-donation-items-other-amount="localDonationItemsOtherAmount"
                    @update:local-donation-items="localDonationItems = $event"
                    @update:local-donation-items-other-amount="localDonationItemsOtherAmount = $event"
                    @update:local-amount="localAmount = $event"
                  />
                  <app-button @click="donateButtonClicked" class="w-full primary">
                    <h3>{{ donateButtonText }}</h3>
                  </app-button>
                </div>
              </div>
            </div>
            <double-logo theme="grayscale" class="mt-4" />
          </form-layout>
        </div>
      </checkout-layout>
    </Iframe>
  </div>
</template>
